import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { Box, Card, CardContent, CircularProgress, Alert, useMediaQuery } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { leadsApi } from '@/api/leads';
import { streamApi } from '@/api/stream';
import { chatApi } from '@/api/chat';
import { teamApi } from '@/api/team';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '@/hooks/useAuth';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  LeadsHeader,
  LeadsFilterBar,
  LeadsTable,
  LeadsCard,
} from '@/components/leads';

export const Leads: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize filters from URL params
  const [search, setSearch] = useState<string>(() => searchParams.get('search') || '');
  const [streams, setStreams] = useState<string[]>(() => {
    const streamsParam = searchParams.get('streams');
    return streamsParam ? streamsParam.split(',').filter(Boolean) : [];
  });
  const [categories, setCategories] = useState<string[]>(() => {
    const categoriesParam = searchParams.get('categories');
    return categoriesParam ? categoriesParam.split(',').filter(Boolean) : [];
  });
  // Initialize operator filter as single value
  const [operatorFilter, setOperatorFilter] = useState<string | null>(() => {
    const operatorParam = searchParams.get('operator_uuid') || searchParams.get('operators'); // Support legacy 'operators' param
    return operatorParam || null;
  });
  // Initialize team filter as single value
  const [teamFilter, setTeamFilter] = useState<string | null>(() => {
    const teamParam = searchParams.get('team_uuid');
    return teamParam || null;
  });
  const [activityFilter, setActivityFilter] = useState<string | null>(() => {
    return searchParams.get('activity') || null;
  });
  const [happenedAfter, setHappenedAfter] = useState<string>(() => {
    return searchParams.get('happened_after') || '';
  });
  const [happenedBefore, setHappenedBefore] = useState<string>(() => {
    return searchParams.get('happened_before') || '';
  });
  const [sortField, setSortField] = useState<string>(() => {
    return searchParams.get('sort_field') || 'created_at';
  });
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() => {
    return (searchParams.get('sort_direction') as 'asc' | 'desc') || 'desc';
  });

  // Debounced search term for URL updates
  const searchDebounced = useDebouncedValue(search, 500);
  
  // Track if we're updating from internal state to avoid circular updates
  const isInternalUpdateRef = useRef(false);

  // Check if user is admin/staff
  const isAdmin = user?.is_staff || user?.is_superuser;

  const availableCategories = useMemo(() => streamApi.getStepCategories(), []);
  const availableActivities = useMemo(() => streamApi.getActivityFilters(), []);
  const sortOptions = useMemo(() => streamApi.getLeadsSortOptions(), []);

  // Options: experiments (streams)
  // Include user key in query key to ensure proper data refresh on impersonation
  const { data: streamsData } = useQuery({
    queryKey: ['streams-options', user?.key],
    queryFn: async () => {
      const res = await streamApi.getStreams({ max_results: 100 });
      return res.output.items as Array<{ key: string; name: string }>;
    },
  });

  // Fetch teams for filter - fetch all teams user has access to (any role: admin, manager, member, owner)
  const {
    data: teamsPages,
    fetchNextPage: fetchNextTeamsPage,
    hasNextPage: hasNextTeamsPage,
    isFetchingNextPage: isFetchingNextTeamsPage,
    isLoading: isLoadingTeams,
  } = useInfiniteQuery({
    queryKey: ['teams-all', user?.key],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      teamApi.getTeams({
        page_token: pageParam,
        max_results: 50,
        any_member_uuid: isAdmin ? undefined : user?.key, // For non-admins, only teams they have access to
      }),
    getNextPageParam: (lastPage) => lastPage.output.next_page_token ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled: !!user,
  });

  // Flatten all teams from all pages
  const allTeams = teamsPages?.pages.flatMap((p) => p.output.items) ?? [];

  // Auto-fetch all pages of teams
  useEffect(() => {
    if (hasNextTeamsPage && !isFetchingNextTeamsPage && !isLoadingTeams) {
      fetchNextTeamsPage();
    }
  }, [hasNextTeamsPage, isFetchingNextTeamsPage, isLoadingTeams, fetchNextTeamsPage]);

  // Fetch operators for filter - backend will filter based on user access
  const {
    data: operatorsPages,
    fetchNextPage: fetchNextOperatorsPage,
    hasNextPage: hasNextOperatorsPage,
    isFetchingNextPage: isFetchingNextOperatorsPage,
    isLoading: isLoadingOperators,
  } = useInfiniteQuery({
    queryKey: ['operators-all', user?.key],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      chatApi.getProjectOwners({ page_token: pageParam, max_results: 50 }),
    getNextPageParam: (lastPage) => lastPage.next_page_token ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled: !!user,
  });

  // Flatten all operators from all pages
  const allOperators = operatorsPages?.pages.flatMap((p) => p.items) ?? [];

  // Auto-fetch all pages of operators
  useEffect(() => {
    if (hasNextOperatorsPage && !isFetchingNextOperatorsPage && !isLoadingOperators) {
      fetchNextOperatorsPage();
    }
  }, [hasNextOperatorsPage, isFetchingNextOperatorsPage, isLoadingOperators, fetchNextOperatorsPage]);

  // Update URL params when filters change
  const updateURLParams = useCallback((updates: {
    search?: string;
    streams?: string[];
    categories?: string[];
    operator_uuid?: string | null;
    team_uuid?: string | null;
    activity?: string | null;
    happened_after?: string;
    happened_before?: string;
    sort_field?: string;
    sort_direction?: string;
  }) => {
    isInternalUpdateRef.current = true;
    const newSearchParams = new URLSearchParams(searchParams);
    
    if (updates.search !== undefined) {
      if (updates.search) {
        newSearchParams.set('search', updates.search);
      } else {
        newSearchParams.delete('search');
      }
    }
    
    if (updates.streams !== undefined) {
      if (updates.streams.length > 0) {
        newSearchParams.set('streams', updates.streams.join(','));
      } else {
        newSearchParams.delete('streams');
      }
    }
    
    if (updates.categories !== undefined) {
      if (updates.categories.length > 0) {
        newSearchParams.set('categories', updates.categories.join(','));
      } else {
        newSearchParams.delete('categories');
      }
    }
    
    if (updates.operator_uuid !== undefined) {
      if (updates.operator_uuid) {
        newSearchParams.set('operator_uuid', updates.operator_uuid);
      } else {
        newSearchParams.delete('operator_uuid');
        newSearchParams.delete('operators'); // Also remove legacy param
      }
    }
    
    if (updates.team_uuid !== undefined) {
      if (updates.team_uuid) {
        newSearchParams.set('team_uuid', updates.team_uuid);
      } else {
        newSearchParams.delete('team_uuid');
      }
    }

    if (updates.activity !== undefined) {
      if (updates.activity) {
        newSearchParams.set('activity', updates.activity);
      } else {
        newSearchParams.delete('activity');
      }
    }

    if (updates.happened_after !== undefined) {
      if (updates.happened_after) {
        newSearchParams.set('happened_after', updates.happened_after);
      } else {
        newSearchParams.delete('happened_after');
      }
    }

    if (updates.happened_before !== undefined) {
      if (updates.happened_before) {
        newSearchParams.set('happened_before', updates.happened_before);
      } else {
        newSearchParams.delete('happened_before');
      }
    }

    if (updates.sort_field !== undefined) {
      if (updates.sort_field && updates.sort_field !== 'created_at') {
        newSearchParams.set('sort_field', updates.sort_field);
      } else {
        newSearchParams.delete('sort_field');
      }
    }

    if (updates.sort_direction !== undefined) {
      if (updates.sort_direction && updates.sort_direction !== 'desc') {
        newSearchParams.set('sort_direction', updates.sort_direction);
      } else {
        newSearchParams.delete('sort_direction');
      }
    }

    setSearchParams(newSearchParams, { replace: true });
    setTimeout(() => {
      isInternalUpdateRef.current = false;
    }, 0);
  }, [searchParams, setSearchParams]);

  // Update URL when debounced search term changes
  useEffect(() => {
    updateURLParams({ search: searchDebounced });
  }, [searchDebounced, updateURLParams]);

  // Update URL when other filters change
  useEffect(() => {
    updateURLParams({ streams });
  }, [streams, updateURLParams]);

  useEffect(() => {
    updateURLParams({ categories });
  }, [categories, updateURLParams]);

  useEffect(() => {
    updateURLParams({ operator_uuid: operatorFilter });
  }, [operatorFilter, updateURLParams]);

  useEffect(() => {
    updateURLParams({ team_uuid: teamFilter });
  }, [teamFilter, updateURLParams]);

  useEffect(() => {
    updateURLParams({ activity: activityFilter });
  }, [activityFilter, updateURLParams]);

  useEffect(() => {
    updateURLParams({ happened_after: happenedAfter });
  }, [happenedAfter, updateURLParams]);

  useEffect(() => {
    updateURLParams({ happened_before: happenedBefore });
  }, [happenedBefore, updateURLParams]);

  useEffect(() => {
    updateURLParams({ sort_field: sortField });
  }, [sortField, updateURLParams]);

  useEffect(() => {
    updateURLParams({ sort_direction: sortDirection });
  }, [sortDirection, updateURLParams]);

  // Sync state from URL params when URL changes externally (e.g., browser back button)
  useEffect(() => {
    if (isInternalUpdateRef.current) {
      return;
    }

    const urlSearch = searchParams.get('search') || '';
    const urlStreams = searchParams.get('streams') ? searchParams.get('streams')!.split(',').filter(Boolean) : [];
    const urlCategories = searchParams.get('categories') ? searchParams.get('categories')!.split(',').filter(Boolean) : [];
    const urlOperator = searchParams.get('operator_uuid') || searchParams.get('operators') || null; // Support legacy 'operators' param
    const urlTeam = searchParams.get('team_uuid') || null;
    const urlActivity = searchParams.get('activity') || null;
    const urlHappenedAfter = searchParams.get('happened_after') || '';
    const urlHappenedBefore = searchParams.get('happened_before') || '';
    const urlSortField = searchParams.get('sort_field') || 'created_at';
    const urlSortDirection = (searchParams.get('sort_direction') as 'asc' | 'desc') || 'desc';

    if (urlSearch !== search) {
      setSearch(urlSearch);
    }
    if (JSON.stringify(urlStreams) !== JSON.stringify(streams)) {
      setStreams(urlStreams);
    }
    if (JSON.stringify(urlCategories) !== JSON.stringify(categories)) {
      setCategories(urlCategories);
    }
    if (urlOperator !== operatorFilter) {
      setOperatorFilter(urlOperator);
    }
    if (urlTeam !== teamFilter) {
      setTeamFilter(urlTeam);
    }
    if (urlActivity !== activityFilter) {
      setActivityFilter(urlActivity);
    }
    if (urlHappenedAfter !== happenedAfter) {
      setHappenedAfter(urlHappenedAfter);
    }
    if (urlHappenedBefore !== happenedBefore) {
      setHappenedBefore(urlHappenedBefore);
    }
    if (urlSortField !== sortField) {
      setSortField(urlSortField);
    }
    if (urlSortDirection !== sortDirection) {
      setSortDirection(urlSortDirection);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Infinite leads query
  // Include user key in query key to ensure proper data refresh on impersonation
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery({
    queryKey: ['leads', user?.key, streams, categories, searchDebounced, operatorFilter, teamFilter, activityFilter, happenedAfter, happenedBefore, sortField, sortDirection],
    queryFn: ({ pageParam }) =>
      leadsApi.list({
        stream: streams.length ? streams : undefined,
        step_categories: categories.length ? categories : undefined,
        search: searchDebounced || undefined,
        operator_uuid: operatorFilter || undefined,
        team_uuid: teamFilter || undefined,
        activity: activityFilter || undefined,
        happened_after: happenedAfter || undefined,
        happened_before: happenedBefore || undefined,
        sort_field: sortField,
        sort_direction: sortDirection,
        page_token: pageParam,
        max_results: 50,
      }),
    getNextPageParam: (lastPage) => lastPage.output.next_page_token,
    initialPageParam: undefined as string | undefined,
  });

  const items = data?.pages.flatMap((p) => p.output.items) || [];

  // Intersection observer for infinite scroll
  const sentinelRef = useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    });

    const node = sentinelRef.current;
    if (node) observer.observe(node);
    return () => { if (node) observer.unobserve(node); };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleExport = async () => {
    const blob = await leadsApi.exportCsv({
      stream: streams.length ? streams : undefined,
      step_categories: categories.length ? categories : undefined,
      search: search || undefined,
      operator_uuid: operatorFilter || undefined,
      team_uuid: teamFilter || undefined,
      activity: activityFilter || undefined,
      happened_after: happenedAfter || undefined,
      happened_before: happenedBefore || undefined,
      sort_field: sortField,
      sort_direction: sortDirection,
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'leads.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const streamsOptions = useMemo(
    () => (streamsData || []).map((s) => ({ uuid: s.key, name: s.name })),
    [streamsData]
  );

  return (
    <Box sx={{ p: 0.5 }}>
      <LeadsHeader />

      {/* Filters rendered by LeadsFilterBar using EntityMultiSelect internally */}

      <LeadsFilterBar
        search={search}
        onSearchChange={setSearch}
        streams={streams}
        onStreamsChange={setStreams}
        categories={categories}
        onCategoriesChange={setCategories}
        onExport={handleExport}
        availableCategories={availableCategories}
        streamsOptions={streamsOptions}
        operatorFilter={operatorFilter}
        onOperatorFilterChange={setOperatorFilter}
        teamFilter={teamFilter}
        onTeamFilterChange={setTeamFilter}
        activityFilter={activityFilter}
        onActivityFilterChange={setActivityFilter}
        happenedAfter={happenedAfter}
        onHappenedAfterChange={setHappenedAfter}
        happenedBefore={happenedBefore}
        onHappenedBeforeChange={setHappenedBefore}
        availableActivities={availableActivities}
        sortField={sortField}
        onSortFieldChange={setSortField}
        sortDirection={sortDirection}
        onSortDirectionChange={setSortDirection}
        sortOptions={sortOptions}
        allOperators={allOperators}
        allTeams={allTeams}
        isLoadingOperators={isLoadingOperators || isFetchingNextOperatorsPage}
        isLoadingTeams={isLoadingTeams || isFetchingNextTeamsPage}
        hasNextOperatorsPage={hasNextOperatorsPage}
        hasNextTeamsPage={hasNextTeamsPage}
        onFetchNextOperatorsPage={fetchNextOperatorsPage}
        onFetchNextTeamsPage={fetchNextTeamsPage}
      />

      {isError ? (
        <Alert severity="error">Failed to load leads.</Alert>
      ) : (
        <Card>
          <CardContent sx={{ p: 2 }}>
            {!isMobile ? (
              <LeadsTable items={items} onRowClick={(uuid) => navigate(`/leads/${uuid}`)} />
            ) : (
              <LeadsCard items={items} onCardClick={(uuid) => navigate(`/leads/${uuid}`)} />
            )}

            {/* Infinite scroll sentinel */}
            <Box ref={sentinelRef} sx={{ height: 24 }} />
            {(isLoading || isFetchingNextPage) && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} />
            </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}; 